'use client';

import React, { useState, useRef, useEffect, useMemo } from 'react';

const getAllDescendantValues = (node) => {
  let values = [Number(node.value)];
  if (Array.isArray(node.children)) {
    node.children.forEach((child) => {
      values = values.concat(getAllDescendantValues(child));
    });
  }
  return values;
};

const flattenTree = (nodes, map = new Map()) => {
  nodes.forEach((node) => {
    map.set(Number(node.value), node);
    if (Array.isArray(node.children)) {
      flattenTree(node.children, map);
    }
  });
  return map;
};

const getCheckedValuesFromTree = (nodes) => {
  const checked = new Set();
  const traverse = (list) => {
    list.forEach((node) => {
      if (node.checked) checked.add(Number(node.value));
      if (Array.isArray(node.children)) traverse(node.children);
    });
  };
  traverse(nodes);
  return checked;
};

export const TreeComponent = ({ data = [], placeholder = '', inputLabel = '', id, handleOnChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedIds, setExpandedIds] = useState(() => {
    const initial = new Set();
    data.forEach((node) => {
      if (node.expanded !== false) initial.add(Number(node.value));
    });
    return initial;
  });

  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const nodeMap = useMemo(() => flattenTree(data), [data]);
  const selectedIds = useMemo(() => getCheckedValuesFromTree(data), [data]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredData = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return data;

    const filterNodes = (nodes) =>
      nodes.reduce((acc, node) => {
        const matchesSelf = node.label?.toLowerCase().includes(query);
        const filteredChildren = Array.isArray(node.children) ? filterNodes(node.children) : [];

        if (matchesSelf) {
          acc.push({ ...node });
        } else if (filteredChildren.length > 0) {
          acc.push({ ...node, children: filteredChildren });
        }
        return acc;
      }, []);

    return filterNodes(data);
  }, [data, searchTerm]);

  const emitChange = (currentNode, nextSelectedSet) => {
    const allSelectedNodes = Array.from(nextSelectedSet).map((val) => {
      const node = nodeMap.get(val);
      return node ? { ...node, value: val } : { value: val };
    });
    handleOnChange(currentNode, allSelectedNodes);
  };

  const handleToggleCheck = (node) => {
    const nextSelected = new Set(selectedIds);
    const val = Number(node.value);
    const isCurrentlyChecked = nextSelected.has(val);

    if (isCurrentlyChecked) {
      nextSelected.delete(val);
    } else {
      nextSelected.add(val);
    }

    emitChange(node, nextSelected);
  };

  const handleRemoveTag = (e, val) => {
    e.stopPropagation();
    const nextSelected = new Set(selectedIds);
    nextSelected.delete(Number(val));
    emitChange(nodeMap.get(Number(val)), nextSelected);
  };

  const toggleExpand = (e, val) => {
    e.stopPropagation();
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(val)) next.delete(val);
      else next.add(val);
      return next;
    });
  };

  const getNodeCheckState = (node) => {
    const val = Number(node.value);
    const isSelfChecked = selectedIds.has(val);

    if (!Array.isArray(node.children) || node.children.length === 0) {
      return isSelfChecked ? 'checked' : 'unchecked';
    }

    const descendantIds = getAllDescendantValues(node).filter((id) => id !== val);
    const checkedDescendants = descendantIds.filter((id) => selectedIds.has(id));

    if (isSelfChecked && checkedDescendants.length === descendantIds.length) {
      return 'checked';
    }
    if (isSelfChecked || checkedDescendants.length > 0) {
      return 'indeterminate';
    }
    return 'unchecked';
  };

  const hasSelections = selectedIds.size > 0;
  const isLabelFloating = hasSelections || isOpen || searchTerm.length > 0;
  const effectiveLabel = inputLabel || placeholder;

  const renderTreeNodes = (nodes, depth = 0) =>
    nodes.map((node) => {
      const val = Number(node.value);
      const hasChildren = Array.isArray(node.children) && node.children.length > 0;
      const isExpanded = expandedIds.has(val) || searchTerm.trim().length > 0;
      const checkState = getNodeCheckState(node);

      return (
        <div key={val} className="px-0 select-none">
          <div
            onClick={() => handleToggleCheck(node)}
            style={{ paddingLeft: `${depth * 18 + 12}px` }}
            className={`flex items-center gap-2 py-1.5 pr-3 hover:bg-black cursor-pointer text-sm transition-colors text-[rgba(255,255,255,0.85)] font-['var(--font-hind)',sans-serif] ${
              checkState === 'checked' ? 'bg-black/40' : ''
            }`}
          >
            {/* Expand / Collapse Indicator */}
            {hasChildren ? (
              <button
                type="button"
                onClick={(e) => toggleExpand(e, val)}
                className="w-4 h-4 flex items-center justify-center text-white/70 hover:text-white font-mono text-xs px-0 select-none"
              >
                {isExpanded ? '−' : '+'}
              </button>
            ) : (
              <span className="w-4 px-0" />
            )}

            {/* Checkbox (Indeterminate / Checked / Unchecked) */}
            <span
              className={`w-[15px] h-[15px] rounded-[2px] flex items-center justify-center shrink-0 transition-colors px-0 ${
                checkState === 'checked' || checkState === 'indeterminate'
                  ? 'bg-[#ec3654] border border-[#ec3654]'
                  : 'bg-white border border-white'
              }`}
            >
              {checkState === 'checked' && (
                <svg
                  className="w-3 h-3 text-white"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  viewBox="0 0 16 16"
                >
                  <path d="M3.5 8.5l3 3 6-6" />
                </svg>
              )}
              {checkState === 'indeterminate' && <span className="w-2 h-[2px] bg-white block rounded-xs px-0" />}
            </span>

            <span className="truncate text-sm text-[rgba(255,255,255,0.85)] font-['var(--font-hind)',sans-serif] px-0">
              {node.label}
            </span>
          </div>

          {hasChildren && isExpanded && <div className="px-0">{renderTreeNodes(node.children, depth + 1)}</div>}
        </div>
      );
    });

  return (
    <div ref={containerRef} id={id} className="relative w-full px-0 selectBox font-['var(--font-hind)',sans-serif]">
      {/* Floating Header Label */}
      {effectiveLabel && (
        <label
          onClick={() => {
            setIsOpen(true);
            inputRef.current?.focus();
          }}
          className={`pointer-events-none absolute left-3 z-10 select-none transition-all duration-200 ${
            isLabelFloating
              ? '-top-[9px] bg-[#1a1a1a] px-1 text-[12px] text-[#10B7FF]'
              : 'top-1/2 -translate-y-1/2 text-[14px] text-white px-0'
          }`}
        >
          {effectiveLabel}
        </label>
      )}

      {/* Input / Box Container */}
      <div
        onClick={() => {
          setIsOpen(true);
          inputRef.current?.focus();
        }}
        className={`flex min-h-[48px] w-full cursor-text flex-wrap items-center gap-1.5 rounded-[4px] border border-solid bg-[#1a1a1a] px-3 py-1.5 transition-colors ${
          isOpen || hasSelections ? 'border-[#10B7FF]' : 'border-[#444] hover:border-[#666]'
        }`}
      >
        {/* Selected Tags */}
        {Array.from(selectedIds).map((val) => {
          const node = nodeMap.get(val);
          if (!node) return null;
          return (
            <span
              key={val}
              className="inline-flex items-center gap-1.5 bg-[#ec3654] border border-[#ec3654] text-white text-[11px] font-['var(--font-hind)',sans-serif] px-2 py-0.5 rounded-[8px] leading-tight select-none"
            >
              <span>{node.label}</span>
              <button
                type="button"
                onClick={(e) => handleRemoveTag(e, val)}
                className="text-white hover:opacity-80 text-[11px] leading-none px-0 ml-0.5 font-normal"
              >
                ✕
              </button>
            </span>
          );
        })}

        {/* Filter Input */}
        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            if (!isOpen) setIsOpen(true);
          }}
          className="flex-1 min-w-[60px] bg-transparent text-[rgba(255,255,255,0.8)] text-sm focus:outline-none py-0.5 px-1 border-none"
        />
      </div>

      {/* Dropdown Menu Popup */}
      {isOpen && (
        <div className="absolute -left-[40px] top-[calc(100%+4px)] min-w-[250px] w-[calc(100%+40px)] max-h-[300px] overflow-y-auto bg-[rgba(26,26,26,1)] border border-[rgba(255,255,255,0.15)] rounded-[4px] shadow-2xl z-[1000] py-2 px-0">
          {filteredData.length > 0 ? (
            renderTreeNodes(filteredData)
          ) : (
            <div className="px-4 py-3 text-sm text-[rgba(255,255,255,0.6)] font-['var(--font-hind)',sans-serif]">
              Brak wyników
            </div>
          )}
        </div>
      )}
    </div>
  );
};
